import React from 'react';
import { Calendar, Car, TicketCheck } from 'lucide-react';
import { CmsAsset, CmsMemory } from '../../integrations/contentful/types';
import H from '../elements/H';
import {
  baseXUnit,
  color,
  fontSize,
  fontWeight,
  imageHoverOpacity
} from '../../styles/theme';
import Spacing from '../common/Spacing';
import MemoryAuthorInfo from '../travellers/MemoryAuthorInfo';
import RouteLink from '../navigation/RouteLink';
import { Paths } from '../../urls/Paths';
import CmsMediaFixedHeight from '../cms/CmsMediaFixedHeight';
import HyperLink from '../elements/HyperLink';
import { arrayHead } from '../../utils/array';

import Paragraph from '../elements/Paragraph';

interface TravelMemoryProps {
  memory: {
    mainImage?: CmsAsset;
    slug: string;
    subtitle?: string;
    title: string;
    startDate: string;
    endDate: string;
    matkanKesto?: string;
    golfInfo?: string;
    tripDepartures?: string;
    vehicleInfo?: string;
  };
  traveller: {
    name: string;
    profilePicture?: CmsAsset;
    slug: string;
  };
}

const MemoryCard: React.FunctionComponent<TravelMemoryProps> = ({
  memory,
  traveller,
  ...rest
}) => {
  return (
    <div data-cy="memory-card" {...rest}>
      <div className="image-and-title">
        <RouteLink
          path={Paths.Memory}
          params={{
            slug: memory.slug
          }}
        >
          <HyperLink
            display="block"
            noLinkStateStyles
            data-testid="memory-link"
          >
            <Spacing bottom={1}>
              <div className="image-container">
                <CmsMediaFixedHeight
                  media={memory.mainImage}
                  fixedHeight={22}
                  borderRadius={1}
                />
                {memory.matkanKesto && (
                  <div className="duration-badge">{memory.matkanKesto}</div>
                )}
              </div>
            </Spacing>
            <Spacing bottom={1}>
              <H tag="h3" styling="h3" data-cy="memory-card-memory-title">
                <strong>{memory.title}</strong>
              </H>
            </Spacing>
          </HyperLink>
        </RouteLink>
      </div>

      {memory.golfInfo || memory.tripDepartures || memory.vehicleInfo ? (
        <div className="info-rows">
          {memory.golfInfo && (
            <div className="info-row">
              <TicketCheck size={20} color={color.purple} strokeWidth={2} />
              <Paragraph small>{memory.golfInfo}</Paragraph>
            </div>
          )}
          {memory.tripDepartures && (
            <div className="info-row">
              <Calendar size={20} color={color.purple} strokeWidth={2} />
              <Paragraph small>{memory.tripDepartures}</Paragraph>
            </div>
          )}
          {memory.vehicleInfo && (
            <div className="info-row">
              <Car size={20} color={color.purple} strokeWidth={2} />
              <Paragraph small>{memory.vehicleInfo}</Paragraph>
            </div>
          )}
        </div>
      ) : (
        <MemoryAuthorInfo
          author={{
            name: traveller.name,
            picture: traveller.profilePicture,
            slug: traveller.slug
          }}
          memoryStartDate={memory.startDate}
          memoryEndDate={memory.endDate}
          subtitle={memory.subtitle}
        />
      )}

      {/*language=CSS*/}
      <style jsx>{`
        .image-container {
          position: relative;
        }
        .duration-badge {
          position: absolute;
          top: ${baseXUnit(1)};
          right: ${baseXUnit(1)};
          background: ${color.white};
          padding: ${baseXUnit(0.5)} ${baseXUnit(1.5)};
          border-radius: ${baseXUnit(1)};
          font-size: ${fontSize.px12};
          font-weight: ${fontWeight.bold};
          color: ${color.dark};
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .image-and-title:hover :global(img) {
          opacity: ${imageHoverOpacity};
        }
        .info-rows {
          display: flex;
          flex-direction: column;
          gap: ${baseXUnit(0.5)};
        }
        .info-row {
          display: flex;
          align-items: center;
          gap: ${baseXUnit(1)};
        }
        .info-row :global(.paragraph) {
          margin: 0;
          color: ${color.gray};
        }
      `}</style>
    </div>
  );
};

export function isMemoryValidToRender(memory: CmsMemory | undefined) {
  if (!memory) {
    return false;
  }
  const traveller = arrayHead(memory.fields.travellers);
  return !!(memory.fields.slug && (traveller || memory.fields.subtitle));
}

export function renderMemoryCardSafely(memory: CmsMemory) {
  const traveller = arrayHead(memory.fields.travellers);
  if (!traveller && !memory.fields.subtitle) {
    return null;
  }
  return (
    <MemoryCard
      data-cy="memory-card"
      key={memory.sys.id}
      memory={{
        ...memory.fields,
        mainImage: memory.fields.mainImage
      }}
      traveller={
        traveller
          ? {
              name: traveller.fields.title,
              profilePicture: traveller.fields.profilePicture,
              slug: traveller.fields.slug
            }
          : {
              name: '',
              slug: ''
            }
      }
    />
  );
}

export default MemoryCard;
